import React, { useEffect, useRef } from "react";
import {useTranslation} from "react-i18next";

interface ErrorDialogProps {
    message: string;
    onClose: () => void;
    autoDismissMs?: number; // optional, e.g. 5000 for 5s
}

export const ErrorDialog: React.FC<ErrorDialogProps> = ({
                                                            message,
                                                            onClose,
                                                            autoDismissMs = 6000,
                                                        }) => {
    const { t } = useTranslation();

    const dialogRef = useRef<HTMLDivElement | null>(null);
    const closeButtonRef = useRef<HTMLButtonElement | null>(null);

    // Auto dismiss
    useEffect(() => {
        if (!message) return;
        const timer = setTimeout(onClose, autoDismissMs);
        return () => clearTimeout(timer);
    }, [message, onClose, autoDismissMs]);

    // Close on Escape key
    useEffect(() => {
        const handleKey = (e: KeyboardEvent) => {
            if (e.key === "Escape") onClose();
        };
        window.addEventListener("keydown", handleKey);
        return () => window.removeEventListener("keydown", handleKey);
    }, [onClose]);

    // Focus the Close button when the dialog appears
    useEffect(() => {
        const current = closeButtonRef.current;
        if (message && current) {
            current.focus();
        }
    }, [message]);

    if (!message) return null;

    return (
        <div
            className="fixed inset-0 flex items-center justify-center bg-black/50 z-50"
            role="dialog"
            aria-modal="true"
            aria-labelledby="error-title"
            ref={dialogRef}
        >
            <div
                className="bg-white rounded-lg shadow-lg p-6 w-96 text-center outline-none"
                tabIndex={-1}
            >
                <h2 id="error-title" className="text-xl font-semibold mb-4 text-red-600">
                    {t('errors.genericError')}
                </h2>
                <p className="text-gray-800 mb-6 whitespace-pre-line">{message}</p>
                <button
                    ref={closeButtonRef}
                    className="bg-blue-600 hover:bg-blue-800 text-white font-semibold px-4 py-2 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                    onClick={onClose}
                >
                    Close
                </button>
            </div>
        </div>
    );
};
